import { buildApiUrl } from "../../utils/api";
import { getCookie } from "../../utils/cookies";

const ADDRESS_API_PATH = "/api/v1/customer/address";

const parseNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const getAddressId = (address) => address?.id ?? address?.address_id ?? address?.customer_address_id;

const normalizeAddressPayload = (addressData = {}) => {
  const address =
    addressData.address ||
    [addressData.street, addressData.locality].filter(Boolean).join(", ") ||
    addressData.addressLine1 ||
    "";

  const payload = {
    address: String(address).trim(),
    city: String(addressData.city || "").trim(),
    state: String(addressData.state || "").trim(),
    pincode: String(addressData.pincode || "").trim(),
    mobile: String(addressData.mobile || addressData.phone || "").trim(),
    latitude: parseNumber(addressData.latitude),
    longitude: parseNumber(addressData.longitude),
    is_default: Boolean(addressData.is_default),
  };

  const gstNumber = String(addressData.gst_number ?? addressData.gstin ?? "").trim().toUpperCase();
  payload.gst_number = gstNumber || null;

  if (addressData.name?.trim())         payload.name         = addressData.name.trim();
  if (addressData.email?.trim())        payload.email        = addressData.email.trim();
  if (addressData.address_type?.trim()) payload.address_type = addressData.address_type.trim();

  if (!payload.address) throw new Error("Address is required");
  if (!payload.city)    throw new Error("City is required");
  if (!payload.state)   throw new Error("State is required");
  if (!payload.pincode) throw new Error("Pincode is required");
  if (!payload.mobile)  throw new Error("Mobile number is required");

  return payload;
};

const normalizeAddress = (address = {}) => {
  const id = getAddressId(address);
  return {
    ...address,
    id,
    address_id: id,
    name: address.name || "",
    email: address.email || "",
    mobile:
      address.mobile ||
      address.phone ||
      address.number ||
      address.mobile_number ||
      address.phone_number ||
      address.contact_number ||
      "",
    phone:
      address.phone ||
      address.mobile ||
      address.number ||
      address.phone_number ||
      address.mobile_number ||
      address.contact_number ||
      "",
    address_type: address.address_type || address.type || "Home",
    address: address.address || "",
    street: address.address || address.street || "",
    locality: address.locality || "",
    city: address.city || "",
    state: address.state || "",
    pincode: address.pincode || "",
    gst_number: address.gst_number ?? address.gstin ?? null,
    gstin: address.gst_number ?? address.gstin ?? "",
    latitude: parseNumber(address.latitude),
    longitude: parseNumber(address.longitude),
    is_default: Boolean(address.is_default),
  };
};

const normalizeAddressList = (data) => {
  const addressData = data?.data ?? data;
  const addresses =
    addressData?.addresses ||
    addressData?.address ||
    addressData?.user_addresses ||
    (Array.isArray(addressData) ? addressData : []);

  return Array.isArray(addresses) ? addresses.map(normalizeAddress) : [];
};

const getAuthHeaders = (withJson = false) => {
  const token = getCookie("authToken");
  return {
    ...(withJson ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const readJson = async (response) => {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    // Server returned HTML (e.g. 404/405 page) — surface the HTTP status as the error
    throw new Error(`Server error ${response.status}: ${response.statusText || 'Unexpected response'}`);
  }
};

export const createAddressesSlice = (set, get) => ({
  user_addresses: [],
  addresses_loading: false,
  addresses_error: null,
  selected_address_id: null,
  setSelectedAddressId: (id) => set({ selected_address_id: id }),

  fetchUserAddresses: async () => {
    set({ addresses_loading: true, addresses_error: null });

    try {
      const response = await fetch(buildApiUrl(ADDRESS_API_PATH), {
        headers: getAuthHeaders(),
      });
      const data = await readJson(response);

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to fetch addresses");
      }

      const addresses = normalizeAddressList(data);
      set({ user_addresses: addresses, addresses_loading: false });
      return addresses;
    } catch (error) {
      set({ addresses_error: error.message, addresses_loading: false });
      console.error("Error fetching addresses:", error);
      return [];
    }
  },

  addAddress: async (addressData) => {
    set({ addresses_loading: true, addresses_error: null });

    try {
      const payload = normalizeAddressPayload(addressData);
      const response = await fetch(buildApiUrl(ADDRESS_API_PATH), {
        method: "POST",
        headers: getAuthHeaders(true),
        body: JSON.stringify(payload),
      });
      const data = await readJson(response);

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to add address");
      }

      const createdAddress = normalizeAddress(
        data.data?.address ?? data.data ?? data.address ?? payload
      );

      // Always re-fetch from API to keep store in sync with server
      await get().fetchUserAddresses();

      return createdAddress;
    } catch (error) {
      set({ addresses_error: error.message, addresses_loading: false });
      console.error("Error adding address:", error);
      throw error;
    }
  },

  updateAddress: async (addressId, addressData) => {
    set({ addresses_loading: true, addresses_error: null });

    try {
      const payload = normalizeAddressPayload(addressData);

      const response = await fetch(buildApiUrl(`${ADDRESS_API_PATH}/${addressId}`), {
        method: "PATCH",
        headers: getAuthHeaders(true),
        body: JSON.stringify(payload),
      });

      const data = await readJson(response);

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to update address");
      }

      await get().fetchUserAddresses();

      return normalizeAddress(
        data.data?.address ?? data.data ?? data.address ?? { ...payload, id: addressId }
      );
    } catch (error) {
      set({ addresses_error: error.message, addresses_loading: false });
      console.error("Error updating address:", error);
      throw error;
    }
  },

  deleteAddress: async (addressId) => {
    set({ addresses_loading: true, addresses_error: null });

    try {
      const response = await fetch(buildApiUrl(`${ADDRESS_API_PATH}/${addressId}`), {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      const data = await readJson(response);

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to delete address");
      }

      set((state) => ({
        user_addresses: state.user_addresses.filter(
          (address) => String(address.id) !== String(addressId)
        ),
        addresses_loading: false,
      }));

      return true;
    } catch (error) {
      set({ addresses_error: error.message, addresses_loading: false });
      console.error("Error deleting address:", error);
      throw error;
    }
  },

  setDefaultAddress: async (addressId) => {
    const address = get().user_addresses.find((item) => String(item.id) === String(addressId));
    if (!address) return null;

    return get().updateAddress(addressId, { ...address, is_default: true });
  },
});
